import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { apiService } from '../services/apiService';
import { Submission } from '../types';
import { Button } from '../components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card';
import { CheckCircle, Clock, AlertCircle, Edit } from 'lucide-react';

export const ManagerDashboard: React.FC = () => {
    const { user } = useAuth();
    const [submissions, setSubmissions] = useState<Submission[]>([]);
    const [reviews, setReviews] = useState<{ [key: string]: any }>({});

    useEffect(() => {
        if (user) {
            apiService.getTeamSubmissions().then(async (data) => {
                // Filter to only show Submitted and Reviewed submissions (exclude Draft)
                const filteredSubmissions = data.filter(sub =>
                    sub.status === 'Submitted' || sub.status === 'Reviewed'
                );
                setSubmissions(filteredSubmissions);

                // Fetch review status for each submission
                const reviewsData: { [key: string]: any } = {};
                await Promise.all(
                    filteredSubmissions.map(async (sub) => {
                        const review = await apiService.getReviewBySubmissionId(sub.id);
                        if (review) {
                            reviewsData[sub.id] = review;
                        }
                    })
                );
                setReviews(reviewsData);
            });
        }
    }, [user]);

    const getStatusIcon = (status: string) => {
        switch (status) {
            case 'Submitted': return <Clock className="h-5 w-5 text-blue-500" />;
            case 'Reviewed': return <CheckCircle className="h-5 w-5 text-green-500" />;
            default: return <AlertCircle className="h-5 w-5 text-gray-500" />;
        }
    };

    const getReviewStatusBadge = (submissionId: string) => {
        const review = reviews[submissionId];
        if (!review) {
            return <span className="px-2 py-1 bg-yellow-100 text-yellow-800 rounded text-xs font-medium">Not Started</span>;
        }
        if (review.status === 'Completed') {
            return <span className="px-2 py-1 bg-green-100 text-green-800 rounded text-xs font-medium">Review Completed</span>;
        }
        if (review.status === 'In Review') {
            return <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs font-medium flex items-center gap-1">
                <Edit className="h-3 w-3" />
                In Progress
            </span>;
        }
        return null;
    };

    const isReviewCompleted = (submissionId: string) => {
        const review = reviews[submissionId];
        return review?.status === 'Completed';
    };

    return (
        <div className="space-y-6">
            <h1 className="text-3xl font-bold tracking-tight text-gray-900">Team Reviews</h1>

            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                {submissions.map((submission) => {
                    const reviewCompleted = isReviewCompleted(submission.id);

                    return (
                        <Card key={submission.id} className="hover:shadow-md transition-shadow">
                            <CardHeader className="pb-4">
                                <CardTitle className="text-lg flex justify-between items-center">
                                    <span>{submission.engineerName}</span>
                                    {getStatusIcon(submission.status)}
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                <div className="space-y-2 text-sm text-gray-600">
                                    <p className="font-medium text-gray-900">{submission.period} ({submission.periodType})</p>
                                    <p>Skills Submitted: {submission.skillCount || 0}</p>
                                    <p>Submission: <span className="font-medium">{submission.status}</span></p>
                                    <div className="flex items-center gap-2">
                                        <span>Review:</span>
                                        {getReviewStatusBadge(submission.id)}
                                    </div>
                                </div>
                                <div className="mt-4">
                                    <Link to={`/manager/review/${submission.id}`} className="w-full">
                                        <Button
                                            className="w-full"
                                            variant={reviewCompleted ? 'secondary' : 'primary'}
                                            disabled={reviewCompleted}
                                        >
                                            {reviewCompleted ? 'View Review' : (reviews[submission.id] ? 'Continue Review' : 'Start Review')}
                                        </Button>
                                    </Link>
                                </div>
                            </CardContent>
                        </Card>
                    );
                })}
                {submissions.length === 0 && (
                    <div className="col-span-full text-center py-12 text-gray-500">
                        No pending submissions from your team.
                    </div>
                )}
            </div>
        </div>
    );
};
